import endpoints from "../../../../utils/endpoints";
import type { FlowStep, OrderSnapshot, StepRuntime } from "../types";
import { FlowStepError, StepSkippedError } from "../engine/errors";
import { garaazTransitionPath, requireChild, verifyEq, waitForEventWithGate, waitForSnapshot } from "./common";

export const STEP_INVOICE_EVENT = "invoice_event";
export const STEP_READY_TO_DISPATCH = "ready_to_dispatch";
export const STEP_CREATE_SHIPMENT = "create_shipment";
export const STEP_ASSIGN_RIDER = "assign_rider";
export const STEP_MARK_DELIVERED = "mark_delivered";
export const STEP_DELIVERED_EVENT = "delivered_event";

const POST_R2D_STATUSES = ["READY_TO_DISPATCH", "DISPATCHED", "SHIPPED", "DELIVERED"];

export const invoiceEventStep: FlowStep = {
  id: STEP_INVOICE_EVENT,
  name: "Wait for Garaaz invoice event",
  targetLabel: "Garaaz invoice created (event)",
  description:
    "Atlas trigger consumes INVOICE_UPLOADED / REVIEWED_INVOICE_UPLOADED and writes order.invoiceInfo. Mandatory for Trading (R2D guard), optional for Facilitation.",
  actor: "EVENT",
  kind: "event",
  optional: true,
  estimateMs: [5000, 45000],
  shouldSkip: (ctx) =>
    ctx.snapshot?.child?.hasGaraazInvoice ? "Garaaz invoice already present on order" : null,
  execute: async (rt) => {
    const policy = rt.ctx.config.invoiceEventPolicy;
    const isTrading = rt.ctx.config.transactionType === "Trading";
    if (policy === "skip" && !isTrading) {
      throw new StepSkippedError("Config: invoice event wait disabled");
    }
    if (policy === "ask") {
      const choice = await rt.decide({
        title: "Wait for Garaaz invoice event?",
        description: isTrading
          ? "Trading orders need order.invoiceInfo before READY_TO_DISPATCH. Waiting is recommended."
          : "Facilitation orders can reach READY_TO_DISPATCH without a Garaaz invoice. You may continue without waiting.",
        options: [
          { id: "wait", label: "Wait for invoice event", tone: "primary" },
          { id: "continue", label: "Continue without waiting", tone: "neutral" },
        ],
      });
      if (choice === "continue") {
        throw new StepSkippedError("User chose to continue without waiting for the invoice event");
      }
    }
    await waitForEventWithGate(rt, {
      trigger: "INVOICE_UPLOADED / REVIEWED_INVOICE_UPLOADED event → Atlas invoice trigger",
      expected: "order.invoiceInfo present",
      check: (s) => Boolean(s.child?.hasGaraazInvoice),
      describe: (s) =>
        `invoiceInfo=${s.child?.hasGaraazInvoice ? s.child.garaazInvoiceNo || "present" : "absent"}, supplier=${s.child?.supplierStatus}`,
      allowContinue: !isTrading,
    });
  },
  verify: (s) => ({
    ok: Boolean(s.child?.hasGaraazInvoice),
    expected: "order.invoiceInfo present",
    actual: s.child?.hasGaraazInvoice ? `invoiceNo ${s.child.garaazInvoiceNo || "present"}` : "absent",
  }),
};

export const readyToDispatchStep: FlowStep = {
  id: STEP_READY_TO_DISPATCH,
  name: "Mark READY_TO_DISPATCH",
  targetLabel: "Order READY_TO_DISPATCH",
  description:
    "OrCA transitions the order (garaaz_transition?action=READY_TO_DISPATCH). Skipped when AUTO_R2D_ON_SUPPLIER_READY already moved it.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [1500, 6000],
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const current = rt.ctx.snapshot?.child?.orderStatus;
    if (current && POST_R2D_STATUSES.includes(current)) {
      throw new StepSkippedError(`Order already ${current} (auto R2D event)`);
    }
    await rt.api({
      label: "READY_TO_DISPATCH",
      method: "POST",
      path: garaazTransitionPath(child.orderId, "READY_TO_DISPATCH"),
      body: {},
    });
    await waitForSnapshot(rt, {
      trigger: "READY_TO_DISPATCH transition",
      expected: "order.orderStatus = READY_TO_DISPATCH",
      check: (s) => POST_R2D_STATUSES.includes(s.child?.orderStatus || ""),
      describe: (s) => `order=${s.child?.orderStatus}`,
      maxAttempts: 3,
    });
  },
  verify: (s) => verifyEq("order.orderStatus", POST_R2D_STATUSES, s.child?.orderStatus),
};

const POST_DISPATCH_STATUSES = ["DISPATCHED", "SHIPPED", "DELIVERED"];

/**
 * Multi-hop logistics: a shipment needs a hop destination on logisticsPath,
 * which the R2D screen saves before DISPATCH. The tester's single hop goes to
 * the customer. Self Pickup needs none (the backend stamps it at R2D).
 */
const saveCustomerHopDestination = async (rt: StepRuntime, orderId: string) => {
  const address: any = rt.ctx.config.shipmentAddress || {};
  const location = address.location;
  try {
    await rt.api({
      label: "Save hop destination (customer)",
      method: "PATCH",
      path: `/v1/orders/${orderId}/logistics-path/destination`,
      body: {
        type: "CUSTOMER",
        customerId: rt.ctx.config.customerId,
        address: {
          addressLine1: address.addressLine1 || address.address || "",
          addressLine2: address.addressLine2 || undefined,
          city: address.city || "",
          state: address.state || "",
          pinCode: address.pinCode ?? address.pincode ?? undefined,
          country: address.country || undefined,
          ...(location?.latitude != null && location?.longitude != null
            ? { location: { latitude: Number(location.latitude), longitude: Number(location.longitude) } }
            : {}),
        },
      },
    });
  } catch (err: any) {
    const response = JSON.stringify(err?.apiCall?.responseBody ?? err?.message ?? "");
    if (err?.apiCall?.status === 400 && /Multi-hop logistics is not enabled/i.test(response)) {
      rt.log("Multi-hop logistics is off: no hop destination needed", "info");
      return;
    }
    throw err;
  }
};

export const assignRiderStep: FlowStep = {
  id: STEP_ASSIGN_RIDER,
  name: "Allocate rider & DISPATCH",
  targetLabel: "Order DISPATCHED",
  description:
    "R2D Self Dispatch (multi-hop): save hop destination on logisticsPath, save dispatchRider, garaaz_transition DISPATCH → DISPATCHED. Create Shipment later from Dispatch dashboard → SHIPPED.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [1000, 5000],
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const current = rt.ctx.snapshot?.child?.orderStatus;
    if (current && POST_DISPATCH_STATUSES.includes(current)) {
      rt.log(`Order already ${current}`, "info");
      return;
    }
    if (current && current !== "READY_TO_DISPATCH") {
      throw new FlowStepError(
        `Cannot DISPATCH from ${current}; expected READY_TO_DISPATCH.`,
        { expected: "READY_TO_DISPATCH", actual: current }
      );
    }

    if (rt.ctx.config.dispatchMode !== "SELF_PICKUP") {
      await saveCustomerHopDestination(rt, child.orderId);
    }

    await rt.api({
      label: "DISPATCH (R2D → Dispatched)",
      method: "POST",
      path: garaazTransitionPath(child.orderId, "DISPATCH"),
      body: {},
    });

    const { rider } = rt.ctx.config;
    const meta: Record<string, unknown> = { dispatchTime: new Date().toISOString() };
    if (rt.ctx.config.dispatchMode !== "SELF_PICKUP") {
      meta.dispatchRider = {
        name: rider.name || "",
        phone: rider.phoneNumber || "",
        vehicleNumber: rider.vehicleNumber || "",
      };
    }
    await rt.api({
      label: "Save dispatch rider / dispatchTime",
      method: "PATCH",
      path: endpoints.updateOrderMetaInfo(child.orderId),
      body: meta,
    });

    await waitForSnapshot(rt, {
      trigger: "DISPATCH transition",
      expected: "order.orderStatus = DISPATCHED",
      check: (s) => POST_DISPATCH_STATUSES.includes(s.child?.orderStatus || ""),
      describe: (s) => `order=${s.child?.orderStatus}, rider=${s.child?.dispatchRider ?? "unset"}`,
      maxAttempts: 3,
    });
    rt.log(`Order ${child.orderNo} DISPATCHED`, "success");
  },
  verify: (s, ctx) => {
    const dispatched = POST_DISPATCH_STATUSES.includes(s.child?.orderStatus || "");
    const riderOk =
      ctx.config.dispatchMode === "SELF_PICKUP" || Boolean(s.child?.dispatchRider);
    return {
      ok: dispatched && riderOk,
      expected:
        ctx.config.dispatchMode === "SELF_PICKUP"
          ? "order.orderStatus = DISPATCHED"
          : `order.orderStatus = DISPATCHED, dispatchRider = ${ctx.config.rider.name}`,
      actual: `order=${s.child?.orderStatus}, rider=${s.child?.dispatchRider ?? "unset"}`,
    };
  },
};

export const createShipmentStep: FlowStep = {
  id: STEP_CREATE_SHIPMENT,
  name: "Create shipment",
  targetLabel: "Shipment created",
  description:
    "Dispatched dashboard: POST /v1/shipments for a DISPATCHED order (SELF_DISPATCH / SELF_PICKUP). Copies the R2D rider onto shipment deliveryTracking. Never uses SYSTEM_PORTER.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [2000, 10000],
  shouldSkip: (ctx) =>
    ctx.snapshot?.child?.shipmentId ? `Shipment ${ctx.snapshot.child.shipmentId} already exists` : null,
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const dispatchMode = rt.ctx.config.dispatchMode;
    if ((dispatchMode as string) === "SYSTEM_PORTER") {
      throw new FlowStepError("Safety stop: SYSTEM_PORTER is not allowed in the tester.");
    }
    const res = await rt.api<any>({
      label: "Create shipment (from DISPATCHED)",
      method: "POST",
      path: `${endpoints.createShipment}?skipGeocodeValidation=true`,
      body: {
        orderNumbers: [child.orderNo],
        deliveryMode: dispatchMode === "SELF_PICKUP" ? "SELF_PICKUP" : "DOOR_DELIVERY",
        dispatchMode,
        additionalComments: "OrCA E2E flow tester",
      },
    });
    if (res.data?.success === false) {
      throw new FlowStepError(
        `Shipment creation rejected: ${res.data.message || res.data.errors?.join(", ")}`,
        { apiCall: res.record, expected: "success = true", actual: JSON.stringify(res.data.errors) }
      );
    }
    const shipmentId = res.data?.data?.shipmentId || res.data?.shipmentId;
    if (!shipmentId) {
      throw new FlowStepError("Shipment response has no shipmentId", { apiCall: res.record });
    }
    rt.ctx.shipment = { shipmentId: String(shipmentId) };
    rt.log(`Shipment ${shipmentId} created (${dispatchMode})`, "success");

    if (dispatchMode !== "SELF_PICKUP") {
      const { rider } = rt.ctx.config;
      await rt.api({
        label: "Copy dispatch rider onto shipment",
        method: "PATCH",
        path: endpoints.updateShipment(shipmentId),
        body: {
          deliveryTracking: {
            deliveryAgent: {
              name: rider.name,
              phoneNumber: rider.phoneNumber,
              vehicleDetails: { vehicleNumber: rider.vehicleNumber },
            },
            timeline: { pickedUpAt: new Date().toISOString() },
          },
        },
      });
    }

    await waitForSnapshot(rt, {
      trigger: "Shipment creation",
      expected: "order.shipmentId set",
      check: (s) => Boolean(s.child?.shipmentId),
      describe: (s) => `order=${s.child?.orderStatus}, shipmentId=${s.child?.shipmentId ?? "unset"}`,
      maxAttempts: 4,
    });
  },
  verify: (s) => ({
    ok: Boolean(s.child?.shipmentId),
    expected: "order.shipmentId set",
    actual: `order.shipmentId = ${s.child?.shipmentId ?? "unset"}, status = ${s.child?.orderStatus}`,
  }),
};

export const markDeliveredStep: FlowStep = {
  id: STEP_MARK_DELIVERED,
  name: "Mark consignment delivered",
  targetLabel: "Consignment delivered",
  description:
    "SELF_DISPATCH: POST /v1/shipments/:id/consignments/:orderNo/mark-delivered. SELF_PICKUP: PATCH shipment status=DELIVERED. Order DELIVERED follows asynchronously.",
  actor: "ORCA",
  kind: "api",
  estimateMs: [1000, 5000],
  shouldSkip: (ctx) =>
    ctx.snapshot?.shipment?.status === "DELIVERED" || ctx.snapshot?.child?.orderStatus === "DELIVERED"
      ? "Shipment/order already delivered"
      : null,
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const shipmentId = rt.ctx.shipment?.shipmentId || rt.ctx.snapshot?.child?.shipmentId;
    if (!shipmentId) throw new FlowStepError("No shipment exists for this order.");
    rt.ctx.shipment = { shipmentId };
    const now = new Date().toISOString();

    if (rt.ctx.config.dispatchMode === "SELF_PICKUP") {
      await rt.api({
        label: "Shipment DELIVERED (self pickup)",
        method: "PATCH",
        path: endpoints.updateShipment(shipmentId),
        body: {
          status: "DELIVERED",
          deliveryTracking: {
            deliveryAgent: { name: rt.ctx.config.rider.name || "Self Pickup", phoneNumber: rt.ctx.config.rider.phoneNumber || "" },
            timeline: { pickedUpAt: now, deliveredAt: now },
          },
        },
      });
    } else {
      await rt.api({
        label: "Mark consignment delivered",
        method: "POST",
        path: `/v1/shipments/${shipmentId}/consignments/${encodeURIComponent(child.orderNo)}/mark-delivered`,
        body: {},
      });
    }
  },
  verify: (s) => ({
    ok: s.shipment?.status === "DELIVERED" || s.shipment?.status === "IN_TRANSIT" || s.child?.orderStatus === "DELIVERED",
    expected: "shipment.status = DELIVERED (or IN_TRANSIT for multi-stop)",
    actual: `shipment.status = ${s.shipment?.status ?? "unknown"}, order = ${s.child?.orderStatus}`,
  }),
};

export const deliveredEventStep: FlowStep = {
  id: STEP_DELIVERED_EVENT,
  name: "Wait for order DELIVERED event",
  targetLabel: "Order DELIVERED",
  description:
    "Atlas trigger MARK_ORDERS_DELIVERED_ON_SHIPMENT_STATUS_UPDATE marks the order DELIVERED. On timeout you may apply the DELIVER transition as a fallback.",
  actor: "EVENT",
  kind: "event",
  estimateMs: [5000, 40000],
  shouldSkip: (ctx) =>
    ctx.snapshot?.child?.orderStatus === "DELIVERED" ? "Order already DELIVERED" : null,
  execute: async (rt) => {
    const child = requireChild(rt.ctx);
    const policy = rt.ctx.config.deliveredEventPolicy;
    const applyDeliverFallback = async () => {
      await rt.api({
        label: "DELIVER transition (fallback)",
        method: "POST",
        path: garaazTransitionPath(child.orderId, "DELIVER"),
        body: {},
      });
      await waitForSnapshot(rt, {
        trigger: "DELIVER transition",
        expected: "order.orderStatus = DELIVERED",
        check: (s) => s.child?.orderStatus === "DELIVERED",
        describe: (s) => `order=${s.child?.orderStatus}`,
        maxAttempts: 3,
      });
    };

    if (policy === "skip") {
      rt.log("Config: skipping event wait, applying DELIVER transition directly", "warn");
      await applyDeliverFallback();
      return;
    }

    await waitForEventWithGate(rt, {
      trigger: "Shipment DELIVERED → MARK_ORDERS_DELIVERED trigger",
      expected: "order.orderStatus = DELIVERED",
      check: (s) => s.child?.orderStatus === "DELIVERED",
      describe: (s) => `order=${s.child?.orderStatus}, shipment=${s.shipment?.status}`,
      allowContinue: false,
      fallback: {
        label: "Apply DELIVER transition",
        description: "Use garaaz_transition?action=DELIVER instead of waiting for the trigger",
        run: applyDeliverFallback,
      },
    });
  },
  verify: (s) => verifyEq("order.orderStatus", "DELIVERED", s.child?.orderStatus),
};
